import { Component, OnInit, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoPipe } from '@jsverse/transloco';
import { mapMimeTypeToAttachmentType } from 'shared/domain';
import { DialogRef, DialogModule, DialogService, ButtonDirective } from 'shared/ui-common';
import { Asset, CurrentSessionResourcesStore, GuidelineDialogType, Resources } from 'shared/util-common';

import { AttachmentFilter, Attachment, DragAndDropInfoDto, DIALOG_DATA } from '@treetop/shared/model';

import { DragAndDropComponent } from '../../../../../shared/ui-common/src/lib/components/drag-and-drop/drag-and-drop.component';
import { AttachmentWithImported } from '../attachment-dialog.service';
import { AssetmanagementService } from '../services/assetmanagement.service';

export type AttachmentDialogData = {
  attachedResources: Attachment[];
  deletedImportedAttachmentIds?: string[];
};
@Component({
  selector: 'app-attachment-dialog',
  templateUrl: './attachment-dialog.component.html',
  styleUrls: ['./attachment-dialog.component.scss'],
  standalone: true,
  imports: [DialogModule, MatIconModule, DragAndDropComponent, TranslocoPipe, ButtonDirective],
})
export class AttachmentDialogComponent implements OnInit {
  private currentSessionResources = inject(CurrentSessionResourcesStore);
  private assetmanagementService = inject(AssetmanagementService);
  private dialogService = inject(DialogService);
  private dialogRef: DialogRef = inject(DialogRef);
  public data: AttachmentDialogData = inject(DIALOG_DATA) as AttachmentDialogData;

  protected searchTerm = new FormControl<string>('');
  protected availableResources: Attachment[] = [];
  protected attachedResources: Attachment[] = [];
  protected availableResourcesConverted: DragAndDropInfoDto[];
  protected attachedResourcesConverted: DragAndDropInfoDto[];
  protected title: string;
  protected guidelineDialogTypes = GuidelineDialogType;
  protected isUploadInProgress = false;
  protected uploading = false;
  protected file: File;
  protected fileName: string;
  protected deletedImportedAttachmentIds: string[] = [];
  protected loading = false;

  public ngOnInit(): void {
    this.attachedResources = [...this.data.attachedResources];
    this.attachedResourcesConverted = this.mapResources(this.attachedResources);
    this.deletedImportedAttachmentIds = this.data.deletedImportedAttachmentIds;
    this.searchAssets();
  }

  protected get assetsQuery(): AttachmentFilter {
    return {
      searchText: this.searchTerm.value ? this.searchTerm.value : null,
    };
  }

  protected onUploadInProgressChanged(state: boolean) {
    this.isUploadInProgress = state;
  }

  protected onFileNameChanged(name: string) {
    this.fileName = name;
  }

  protected onUploadFile(data: Asset) {
    this.file = data?.file;
    this.fileName = data?.name;
  }

  protected onUpdateResources(data: Resources) {
    this.attachedResourcesConverted = data.attachedResources;
    this.availableResourcesConverted = data.availableResources;
    this.deletedImportedAttachmentIds = data.deletedImportedResourceIds;
  }

  protected onAdd() {
    if (!this.file) {
      return;
    }

    this.uploading = true;

    const asset: Asset = { file: this.file, name: this.fileName };

    if (asset.file && asset.name) {
      const formData = new FormData();
      formData.append('displayName', asset.name ? asset.name : asset.file.name);
      formData.append('file', asset.file);
      this.assetmanagementService.addAttachment(formData).subscribe((response) => {
        if (response.id) {
          this.assetmanagementService.loadAttachments(this.assetsQuery, { itemsPerPage: 1000 }).subscribe((assets: Attachment[]) => {
            const newFile = assets.find((asset) => asset.uniqueId === response.id);
            this.updateAttachedResources();
            this.attachedResources.push(newFile);
            this.attachedResourcesConverted = this.mapResources(this.attachedResources);
            const currentUploads = this.currentSessionResources.currentAssets.state;
            currentUploads.push(newFile);
            this.currentSessionResources.currentAssets.setState(currentUploads);
            this.searchAssets();
            this.uploading = false;
          });
        }
        this.isUploadInProgress = false;
        this.uploading = false;
        this.file = null;
      });
    }
  }

  protected onSearchTermChanged(searchTerm: string) {
    this.searchTerm.setValue(searchTerm);
    this.searchAssets();
  }

  protected onSaveDialog(): void {
    this.updateAttachedResources();
    this.dialogRef.close({ attachedResources: this.attachedResources, deletedImportedResourceIds: this.deletedImportedAttachmentIds });
  }

  protected onCloseDialog(): void {
    this.dialogRef.close();
  }

  private searchAssets(): void {
    this.assetmanagementService.loadAttachments(this.assetsQuery).subscribe((assets: Attachment[]) => {
      const availableResourcesCurrentSearch = assets.filter(
        (asset) => !this.attachedResources.some((alreadyAttached) => asset.id === alreadyAttached.id),
      );
      this.availableResourcesConverted = this.mapResources(availableResourcesCurrentSearch);
      this.availableResources = [...this.availableResources, ...availableResourcesCurrentSearch];
      this.loading = false;
    });
  }

  private mapResources(resources: AttachmentWithImported[]) {
    const dragAndDropInfoArray: DragAndDropInfoDto[] = [];

    resources.filter(Boolean).forEach((asset) => {
      const dragAndDropInfo: DragAndDropInfoDto = new DragAndDropInfoDto();
      dragAndDropInfo.Normal = [];
      dragAndDropInfo.Strong = asset.displayName && asset.displayName != 'null' ? asset.displayName : asset.originalName;
      dragAndDropInfo.ID = asset.id;
      dragAndDropInfo.Normal.push(
        `${asset.displayName && asset.displayName != 'null' ? asset.displayName + ', ' : ''} ${asset.originalName},  ${asset.contentType} `,
      );
      dragAndDropInfo.updateDate = asset.versionId;
      dragAndDropInfo.icon = mapMimeTypeToAttachmentType(asset.contentType);
      dragAndDropInfo.imported = asset.imported;
      dragAndDropInfoArray.push(dragAndDropInfo);
    });

    return dragAndDropInfoArray;
  }

  private updateAttachedResources() {
    this.attachedResources = this.attachedResourcesConverted.map((item) => {
      const foundInAvailable = this.availableResources.find((x) => x.id === item.ID);
      const foundInAttached = this.attachedResources.find((x) => x.id === item.ID);

      return foundInAvailable ? foundInAvailable : foundInAttached;
    });
  }
}
